import type { Settlement } from '../map/settlement';
import { BUILDING_DEFS, type BuildingType } from './buildingDefs';
import { getSettlementTierData } from './settlementTiers';

export function getBuildingCount(settlement: Settlement, building: BuildingType): number {
    switch (building) {
        case 'house':
            return settlement.buildings.houses;
        case 'stoneHouse':
            return settlement.buildings.stoneHouse;
        case 'lumberjackCamp':
            return settlement.buildings.lumberjack.qty;
        case 'sawmill':
            return settlement.buildings.sawmill.qty;
        case 'farm':
            return settlement.buildings.farm.qty;
        case 'huntingHut':
            return settlement.buildings.huntingHut.qty;
        case 'fishingHut':
            return settlement.buildings.fishingHut.qty;
        case 'smokehouse':
            return settlement.buildings.smokehouse.qty;
        case 'weaver':
            return settlement.buildings.weaver.qty;
        case 'blacksmith':
            return settlement.buildings.blacksmith.qty;
        case 'goldMine':
            return settlement.buildings.goldMine.qty;
        case 'mint':
            return settlement.buildings.mint.qty;
        case 'caravanPost':
            return settlement.buildings.caravanPost.qty;
        case 'stable':
            return settlement.buildings.stable.qty;
        case 'quarry':
            return settlement.buildings.quarry.qty;
        case 'masonry':
            return settlement.buildings.masonry.qty;
        case 'charcoalPit':
            return settlement.buildings.charcoalPit.qty;
        case 'ironMine':
            return settlement.buildings.ironMine.qty;
        case 'smelter':
            return settlement.buildings.smelter.qty;
        case 'barracks':
            return settlement.buildings.barracks.qty;
        default:
            return 0;
    }
}

export function getQueuedBuildingCount(settlement: Settlement, building: BuildingType): number {
    return settlement.construction.queue.filter((item) => item.building === building).length;
}

export function getBuildingSlotsUsed(settlement: Settlement): number {
    const existing =
        settlement.buildings.houses +
        settlement.buildings.stoneHouse +
        settlement.buildings.lumberjack.qty +
        settlement.buildings.sawmill.qty +
        settlement.buildings.farm.qty +
        settlement.buildings.huntingHut.qty +
        settlement.buildings.fishingHut.qty +
        settlement.buildings.smokehouse.qty +
        settlement.buildings.weaver.qty +
        settlement.buildings.blacksmith.qty +
        settlement.buildings.goldMine.qty +
        settlement.buildings.mint.qty +
        settlement.buildings.caravanPost.qty +
        settlement.buildings.stable.qty +
        settlement.buildings.quarry.qty +
        settlement.buildings.masonry.qty +
        settlement.buildings.charcoalPit.qty +
        settlement.buildings.ironMine.qty +
        settlement.buildings.smelter.qty +
        settlement.buildings.barracks.qty +
        (settlement.buildings.storage ? 1 : 0);
    const queued = settlement.construction.queue.reduce((sum, item) => {
        const def = BUILDING_DEFS[item.building];
        return sum + (def?.slots ?? 0);
    }, 0);
    return existing + queued;
}

export function getBuildingSlotsLimit(settlement: Settlement): number {
    return getSettlementTierData(settlement.tier).buildingSlots;
}

export function getBuildingLimit(settlement: Settlement, building: BuildingType): number {
    const def = BUILDING_DEFS[building];
    return def.limitByTier[settlement.tier];
}

export function canQueueBuilding(settlement: Settlement, building: BuildingType): boolean {
    const def = BUILDING_DEFS[building];
    const totalCount =
        getBuildingCount(settlement, building) + getQueuedBuildingCount(settlement, building);
    if (totalCount >= def.limitByTier[settlement.tier]) return false;
    const usedSlots = getBuildingSlotsUsed(settlement);
    const limitSlots = getBuildingSlotsLimit(settlement);
    return usedSlots + def.slots <= limitSlots;
}
